import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import {
  useIntl as useReactIntl,
  RawIntlProvider,
  createIntl,
  createIntlCache,
  type IntlShape,
} from 'react-intl';
import { type Locale } from '@mfw/translations';
import config from '@mfw/translations/config';
import { enUS } from 'date-fns/locale/en-US';
import React from 'react';

import { formatRegionDate } from '../utils/regionDate';
import createContextFactory from './createContextFactory';

const intlCache = createIntlCache();

type TranslationsContext = {
  updateLocaleAsync: (locale: Locale) => Promise<void>;
  messages: Record<string, string>;
  adapterLocale: typeof enUS;
  locale: Locale;
};

const Context = createContextFactory({
  devToolsName: 'TranslationsProvider',
  initialValue: { locale: config.defaultLocale } as TranslationsContext,
});

export function useIntl() {
  const response = useReactIntl();
  return {
    ...response,
    locale: response.locale as Locale,
  };
}

export function useTranslationsProvider() {
  return Context.useContext();
}

interface IntlMessagesProps {
  children: React.ReactNode;
}
const IntlMessages = ({ children }: IntlMessagesProps) => {
  const { locale, messages } = useTranslationsProvider();
  // One date rule for the whole app: every `formatDate` / `<FormattedDate>`
  // places the day and month by the viewer's region (see `formatRegionDate`).
  const intl = React.useMemo<IntlShape>(() => {
    const base = createIntl(
      { defaultLocale: config.defaultLocale, locale, messages },
      intlCache,
    );
    const formatDate: IntlShape['formatDate'] = (value, options) => {
      const { format, ...rest } = options ?? {};
      const named = format ? base.formats.date?.[format] : undefined;
      // A Temporal object or nothing falls through to react-intl's own.
      if (
        value === undefined ||
        !(
          typeof value === 'string' ||
          typeof value === 'number' ||
          value instanceof Date
        )
      ) {
        return base.formatDate(value, options);
      }
      return formatRegionDate(value, locale, { ...named, ...rest });
    };
    return { ...base, formatDate };
  }, [locale, messages]);
  return <RawIntlProvider value={intl}>{children}</RawIntlProvider>;
};

interface TranslationsProviderProps {
  children: React.ReactNode;
}
export const TranslationsProvider = ({
  children,
}: TranslationsProviderProps) => {
  const [messages, setMessages] = React.useState<Record<string, string>>({});
  const [locale, setLocale] = React.useState<Locale>(config.defaultLocale);
  const [adapterLocale, setAdapterLocale] = React.useState(enUS);

  const resolve = async (newLocale: Locale) => {
    if (newLocale === 'sr-Latn') {
      const newMessages = await import('@mfw/translations' + '/sr-Latn');
      const newAdapterLocale = await import('date-fns/locale/sr-Latn');
      return {
        adapterLocale: newAdapterLocale.srLatn,
        messages: newMessages.default,
      };
    }
    const newMessages = await import(`@mfw/translations/en-US`);
    return { adapterLocale: enUS, messages: newMessages.default };
  };

  // The latest call wins: this provider's own first load may still be in flight
  // when a tenant's or an account's language arrives, and finishing later must
  // not put the default back.
  const latestUpdate = React.useRef(0);
  const updateLocaleAsync = React.useCallback(async (newLocale: Locale) => {
    latestUpdate.current += 1;
    const update = latestUpdate.current;
    const resolved = await resolve(newLocale);
    if (update !== latestUpdate.current) {
      return;
    }
    setAdapterLocale(resolved.adapterLocale);
    setMessages(resolved.messages);
    setLocale(newLocale);
  }, []);

  // Only if nobody has asked yet: a child's mount effect runs before this one, and
  // a public page with its answer in the cache asks for its language there.
  React.useEffect(() => {
    if (latestUpdate.current === 0) {
      updateLocaleAsync(config.defaultLocale);
    }
  }, [updateLocaleAsync]);

  const providedValue = React.useMemo(() => {
    return {
      adapterLocale,
      locale,
      messages,
      updateLocaleAsync,
    };
  }, [locale, messages, adapterLocale, updateLocaleAsync]);
  return (
    <Context.Provider value={providedValue}>
      <IntlMessages>
        <LocalizationProvider
          adapterLocale={adapterLocale}
          dateAdapter={AdapterDateFns}
        >
          {children}
        </LocalizationProvider>
      </IntlMessages>
    </Context.Provider>
  );
};

export default TranslationsProvider;
